import { useState } from 'react'
import {
	EuiModalBody,
	EuiModalFooter,
	EuiFieldText,
	EuiDatePicker,
	EuiFormRow,
	EuiButton,
	EuiFlexGroup,
	EuiFlexItem,
} from '@equipmentshare/ds2'

import { heapEvent } from '@equipmentshare/heaper'
import { findMaxTime } from '../../../../views/helpers/utils'

import CurrencyField from 'components/CurrencyField/currencyField'
import moment from 'moment'
import useCreateWeightedAverageCostOverrideHandler from 'views/item/effects/createWeightedAverageCostOverride'
import validateForm from './helpers/validateForm'
import { StorePart } from '../../../../types/storePart'

export type OverrideWacProps = {
  item: StorePart,
	onCancel: () => void,
	onSuccess: () => void,
}

export type OverrideWacFormState = {
	wac: string,
	date?: moment.Moment,
	reason: string,
	validation: {
		wac: { isInvalid: boolean, error?: string },
		date: { isInvalid: boolean, error?: string },
		reason: { isInvalid: boolean, error?: string },
	},
}

export default function OverrideWac( { item, onCancel, onSuccess }: OverrideWacProps ) {
	const overrideWac = useCreateWeightedAverageCostOverrideHandler()
	const [ formState, setFormState ] = useState<OverrideWacFormState>( {
		wac: item.weightedAverageCost?.amount?.toString() ?? '',
		reason: '',
		validation: {
			wac: { isInvalid: false },
			date: { isInvalid: false },
			reason: { isInvalid: false },
		}
	} )
	const [ isSaving, setIsSaving ] = useState( false )

	const handleSubmit = async() => {
		heapEvent( 'Inventory - Override WAC Modal - Click - Submit', {} )
		setIsSaving( true )

		const { isInvalid, newFormState } = validateForm( formState )
		if ( isInvalid ) {
			setFormState( newFormState )
			setIsSaving( false )
		} else {
			try {
				await overrideWac(
					formState.date?.toISOString() ?? '',
					item.inventoryLocation.id,
					item.part.id.toString(),
					formState.reason,
					parseFloat( formState.wac ),
			 )
			 onSuccess()
			} catch ( e: unknown ) {
				setIsSaving( false )
			}
		}
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Override WAC Modal - Click - Cancel', {} )
		onCancel()
	}

	const onChangeWac = ( value: string, triggerValidation?: boolean ) => {
		const newFormState = {
			...formState,
			wac: value,
		}

		if ( triggerValidation ) {
			newFormState.validation.wac = {
				isInvalid: false,
			}
		}

		setFormState( {
			...formState,
			wac: value,
		} )
	}

	const onChangeDate = ( date: moment.Moment ) => {
		setFormState( {
			...formState,
			date,
			validation: {
				...formState.validation,
				date: { isInvalid: date.unix() > Math.floor( Date.now() / 1000 ) } }
		} )
	}

	const onChangeReason = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		setFormState( {
			...formState,
			reason: event.currentTarget.value,
			validation: {
				...formState.validation,
				reason: { isInvalid: false },
			}
		} )
	}

	return (
		<>
			<EuiModalBody>
				<EuiFlexGroup direction="column" gutterSize="none">
					<EuiFlexItem grow={false} style={{ width: '221px', marginBottom: '16px' }}>
						<EuiFormRow
							label={<>Weighted average cost (WAC) <span className="required">*</span></>}
							{...formState.validation.wac}
						>
							<CurrencyField
								id="wac"
								onRawChange={onChangeWac}
								value={formState.wac}
								isInvalid={formState.validation.wac.isInvalid}
							/>
						</EuiFormRow>
					</EuiFlexItem>

					<EuiFlexItem grow={false} style={{ width: '221px', marginBottom: '16px' }}>
						<EuiFormRow
							label={<>Date of change <span className="required">*</span></>}
							{...formState.validation.date}
						>
							<EuiDatePicker
								id="date"
								onChange={onChangeDate}
								selected={formState.date}
								isInvalid={formState.validation.date.isInvalid}
								showTimeSelect
								maxDate={moment()}
								maxTime={formState.date !== undefined ? findMaxTime( formState.date ) : moment()}
								minTime={moment().utc().startOf( 'day' )}
							/>
						</EuiFormRow>
					</EuiFlexItem>

					<EuiFlexItem grow={false} style={{ width: '585px' }}>
						<EuiFormRow
							label={<>Reason for change <span className="required">*</span></>}
							{...formState.validation.reason}
						>
							<EuiFieldText
								id="reason"
								onChange={onChangeReason}
								value={formState.reason}
								isInvalid={formState.validation.reason.isInvalid}
							/>
						</EuiFormRow>
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiModalBody>

			<EuiModalFooter>
				<EuiButton
					id="cancelBtn"
					color="text"
					onClick={handleCancel}
				>
          Cancel
				</EuiButton>
				<EuiButton
					id="submitBtn"
					fill
					color="primary"
					className="submit"
					onClick={handleSubmit}
					isLoading={isSaving}
				>
          Submit new WAC
				</EuiButton>
			</EuiModalFooter>
		</>
	)
}
